import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, XCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { EmeraldButton } from "@/components/Bits";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2200;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "unpaid" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", payment_status: "" });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setState(res.data);
        if (res.data.payment_status === "paid") return;
        if (res.data.status === "expired") return;
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState((s) => ({ ...s, status: "timeout" }));
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        const notFound = err?.response?.status === 404;
        if (notFound) {
          setState({ status: "missing", payment_status: "" });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "error", payment_status: "" });
          return;
        }
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";
  const missing = state.status === "missing";

  return (
    <div data-testid="page-payment-success" className="min-h-[70vh] bg-ess-paper">
      <section className="ess-section">
        <div className="ess-container max-w-2xl text-center">
          {!sessionId || missing ? (
            <>
              <XCircle size={56} className="mx-auto text-ess-emerald/40" />
              <h1 className="font-display text-4xl md:text-5xl mt-6 text-ess-emerald">
                No session found.
              </h1>
              <p className="mt-4 text-ess-slate">
                We couldn't locate your checkout session. Please try again from the catalog.
              </p>
              <EmeraldButton as="link" to="/digital-products" className="mt-8">
                Back to Catalog
              </EmeraldButton>
            </>
          ) : paid ? (
            <>
              <CheckCircle2 size={64} className="mx-auto text-ess-emerald" strokeWidth={1.4} />
              <p className="ess-overline mt-6">Payment Confirmed</p>
              <h1 className="font-display text-5xl md:text-6xl mt-3 text-ess-emerald leading-tight">
                Welcome to the Society.
              </h1>
              <p className="mt-5 text-ess-slate">
                Your purchase has been received. A confirmation email and access
                details will arrive shortly.
              </p>
              <div className="mt-3 text-xs text-ess-slate/70">
                {state.amount && (
                  <span>
                    Amount paid:&nbsp;
                    <strong className="text-ess-emerald">
                      {Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: (state.currency || "usd").toUpperCase(),
                      }).format(state.amount)}
                    </strong>
                  </span>
                )}
              </div>
              <div className="mt-10 flex justify-center gap-4 flex-wrap">
                <EmeraldButton as="link" to="/digital-products">
                  Continue Browsing <ArrowRight size={14} className="ml-3" />
                </EmeraldButton>
                <Link
                  to="/"
                  className="text-[12px] uppercase tracking-[0.24em] text-ess-emerald ess-link self-center"
                >
                  Return Home
                </Link>
              </div>
            </>
          ) : expired ? (
            <>
              <Clock size={56} className="mx-auto text-ess-emerald/40" />
              <h1 className="font-display text-4xl mt-6 text-ess-emerald">
                Your session expired.
              </h1>
              <p className="mt-4 text-ess-slate">Please start a new checkout when ready.</p>
              <EmeraldButton as="link" to="/digital-products" className="mt-8">
                Back to Catalog
              </EmeraldButton>
            </>
          ) : (
            <>
              <Loader2
                size={48}
                className="mx-auto text-ess-emerald animate-spin"
                strokeWidth={1.4}
              />
              <h1 className="font-display text-4xl md:text-5xl mt-6 text-ess-emerald">
                Confirming your payment…
              </h1>
              <p className="mt-4 text-ess-slate">
                This usually takes just a few seconds. Please do not close this window.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default PaymentSuccess;
